import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Building2, Lock, Mail, Shield, User, EyeOff, Check } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { useToast } from "@/shared/providers/ToastProvider";
import { ApiError } from "@/shared/lib/apiClient";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { FormField, inputClass, selectClass } from "../components/FormField";
import { createAdminUser, fetchAdminUser, updateAdminUser } from "../api/adminApi";
import { fetchAdminInsurers, type AdminInsurer } from "../api/insurerApi";
import { getAuthToken, getAuthUser, isSuperAdminUser } from "../auth";

type UserFormPageProps = {
  mode: "create" | "edit";
};

export function UserFormPage({ mode }: UserFormPageProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const params = useParams();
  const userId = mode === "edit" ? Number(params.id) : null;
  const labels = t.safecheck.usersPage.create;

  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [password, setPassword] = useState("");
  const [insurers, setInsurers] = useState<AdminInsurer[]>([]);
  const [insurerId, setInsurerId] = useState<number | null>(null);
  const [insurerIds, setInsurerIds] = useState<number[]>([]);
  const [chiefDoctor, setChiefDoctor] = useState(false);
  const [loading, setLoading] = useState(mode === "edit");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [targetIsAdminAccount, setTargetIsAdminAccount] = useState(false);
  const isSuperAdmin = isSuperAdminUser();
  const currentUserId = getAuthUser()?.id ?? null;

  useEffect(() => {
    if (!getAuthToken()) return;
    fetchAdminInsurers()
      .then(setInsurers)
      .catch(() => setInsurers([]));
  }, []);

  useEffect(() => {
    if (mode !== "edit" || !userId || Number.isNaN(userId)) return;
    if (!getAuthToken()) {
      setError("Connexion admin requise.");
      setLoading(false);
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const user = await fetchAdminUser(userId);
        if (cancelled) return;
        const apiRole = user.role;
        const isAdminAccount = apiRole === "admin" || apiRole === "super_admin";
        setTargetIsAdminAccount(isAdminAccount);
        if (isAdminAccount && !isSuperAdmin && currentUserId !== userId) {
          setError("Seul un super administrateur peut modifier ce compte.");
          setLoading(false);
          return;
        }
        setUsername(user.name);
        setEmail(user.email);
        setRole(apiRole);
        setInsurerId(user.insurer_id ?? null);
        setInsurerIds(user.insurer_ids ?? []);
        setChiefDoctor(Boolean(user.is_chief_doctor));
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Utilisateur introuvable.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [mode, userId, isSuperAdmin, currentUserId]);

  if (mode === "edit" && error === "Seul un super administrateur peut modifier ce compte.") {
    return <Navigate to="/dashboard/users" replace />;
  }

  const handleSubmit = async () => {
    setError(null);

    if (!username.trim() || !email.trim() || !role) {
      setError("Tous les champs obligatoires doivent être remplis.");
      return;
    }
    if (role === "insurer" && !insurerId) {
      setError("Choisissez l'assureur de ce compte Assurance.");
      return;
    }
    if ((role === "claims" || role === "auditor") && insurerIds.length === 0) {
      setError("Rattachez ce compte à au moins un assureur.");
      return;
    }
    if (mode === "create" && password.length < 6) {
      setError("Le mot de passe doit contenir au moins 6 caractères.");
      return;
    }
    if (mode === "edit" && password && password.length < 6) {
      setError("Le mot de passe doit contenir au moins 6 caractères.");
      return;
    }
    if (!getAuthToken()) {
      setError("Connexion admin requise.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        username: username.trim(),
        email: email.trim(),
        role,
        insurer_id: role === "insurer" ? insurerId : null,
        insurer_ids: role === "claims" || role === "auditor" ? insurerIds : [],
        is_chief_doctor: role === "auditor" && chiefDoctor,
        ...(password ? { password } : mode === "create" ? { password } : {}),
      };

      if (mode === "create") {
        await createAdminUser({ ...payload, password });
      } else if (userId) {
        await updateAdminUser(userId, payload);
      }

      showToast(t.actions.saved);
      navigate("/dashboard/users");
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Enregistrement impossible.";
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Breadcrumbs
        items={[
          { label: t.safecheck.nav.users, href: "/dashboard/users" },
          { label: mode === "create" ? t.safecheck.nav.addUser : "Modifier" },
        ]}
      />

      <div className="mb-4 flex justify-end">
        <Link
          to="/dashboard/users"
          className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent/20 hover:border-accent"
        >
          <ArrowLeft size={14} />
          {labels.backToList}
        </Link>
      </div>

      <div className="theme-card p-6">
        <h1 className="mb-6 text-xl font-semibold text-foreground">
          {mode === "create" ? labels.formTitle : "Modifier l'utilisateur"}
        </h1>

        {loading ? <p className="mb-4 text-sm text-muted">Chargement…</p> : null}
        {error ? <p className="mb-4 text-sm text-red-600">{error}</p> : null}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FormField label={labels.fields.username} required>
            <div className="relative">
              <User size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
              <input
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                placeholder={labels.placeholders.username}
                className={`${inputClass} pl-9`}
                disabled={loading || saving}
              />
            </div>
          </FormField>

          <FormField label={labels.fields.email} required>
            <div className="relative">
              <Mail size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder={labels.placeholders.email}
                className={`${inputClass} pl-9`}
                disabled={loading || saving}
              />
            </div>
          </FormField>

          <FormField label={labels.fields.role} hint={labels.roleHint} required>
            <div className="relative">
              <Shield size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
              <select
                value={role}
                onChange={(event) => setRole(event.target.value)}
                className={`${selectClass} pl-9`}
                disabled={loading || saving || (mode === "edit" && targetIsAdminAccount && !isSuperAdmin)}
              >
                <option value="">{labels.placeholders.role}</option>
                <option value="insurer">{t.safecheck.usersPage.roles.enterprise}</option>
                <option value="claims">{t.safecheck.usersPage.roles.hospital}</option>
                <option value="auditor">{t.safecheck.usersPage.roles.auditor}</option>
                {isSuperAdmin ? (
                  <option value="admin">{t.safecheck.usersPage.roles.admin}</option>
                ) : null}
              </select>
            </div>
          </FormField>

          {role === "insurer" ? (
            <FormField label="Assureur" hint="Le compte Assurance ne voit que les données de cet assureur." required>
              <div className="relative">
                <Building2 size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
                <select
                  value={insurerId ?? ""}
                  onChange={(event) => setInsurerId(event.target.value ? Number(event.target.value) : null)}
                  className={`${selectClass} pl-9`}
                  disabled={loading || saving}
                >
                  <option value="">Choisir un assureur</option>
                  {insurers.map((insurer) => (
                    <option key={insurer.id} value={insurer.id}>
                      {insurer.name}
                    </option>
                  ))}
                </select>
              </div>
            </FormField>
          ) : null}

          {role === "claims" || role === "auditor" ? (
            <FormField label="Assureurs" hint="Assureurs pour lesquels ce compte SafeCheck travaille." required>
              <div className="flex flex-col gap-2 rounded-lg border border-border p-3">
                {insurers.length === 0 ? (
                  <p className="text-sm text-muted">Aucun assureur. Créez-en un dans Assureurs.</p>
                ) : (
                  insurers.map((insurer) => (
                    <label key={insurer.id} className="flex items-center gap-2 text-sm text-foreground">
                      <input
                        type="checkbox"
                        checked={insurerIds.includes(insurer.id)}
                        onChange={(event) =>
                          setInsurerIds((current) =>
                            event.target.checked
                              ? [...current, insurer.id]
                              : current.filter((id) => id !== insurer.id),
                          )
                        }
                        disabled={loading || saving}
                      />
                      {insurer.name}
                    </label>
                  ))
                )}
              </div>
            </FormField>
          ) : null}

          {role === "auditor" ? (
            <label className="flex items-center gap-2 text-sm text-foreground">
              <input type="checkbox" checked={chiefDoctor} onChange={(event) => setChiefDoctor(event.target.checked)} />
              Médecin en chef : décide seul des demandes d'un assuré à 90 % de sa limite annuelle
            </label>
          ) : null}

          <FormField
            label={labels.fields.password}
            hint={mode === "edit" ? "Laisser vide pour ne pas changer" : labels.passwordHint}
            required={mode === "create"}
          >
            <div className="relative">
              <Lock size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder={labels.placeholders.password}
                className={`${inputClass} pl-9 pr-9`}
                disabled={loading || saving}
              />
              <EyeOff size={14} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted" />
            </div>
          </FormField>
        </div>

        <div className="mt-8 flex items-center justify-between">
          <Link
            to="/dashboard/users"
            className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent/20 hover:border-accent"
          >
            {t.safecheck.cancel}
          </Link>
          <button
            type="button"
            disabled={loading || saving}
            onClick={() => void handleSubmit()}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-60"
          >
            <Check size={15} />
            {saving ? "Enregistrement…" : labels.submit}
          </button>
        </div>
      </div>
    </>
  );
}

export function AddUserPage() {
  return <UserFormPage mode="create" />;
}

export function EditUserPage() {
  return <UserFormPage mode="edit" />;
}
